import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import userService from "services/userService";

// Initial state for the users slice
const initialState = {
  loading: false,
  users: [],
  groups: [],
  permissions: [],
  selectedUser: null, // Add this to store the fetched user by ID
  message: "",
  showMessage: false,
  redirect: "",
};

// Async thunk to fetch users
export const getUsers = createAsyncThunk(
  "users/getusers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getUsers();
      console.log(response, "response");
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getUserProfileById = createAsyncThunk(
  "auth/getUserProfileById",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserById(userId);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// Async thunk to fetch users
export const postUsers = createAsyncThunk(
  "users/postusers",
  async ({ data, headers }, { rejectWithValue }) => {
    try {
      const response = await userService.createuser({
        params: data,
        headers: headers,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// Async thunk to fetch users
export const updateUsers = createAsyncThunk(
  "users/updateusers",
  async ({ data, userId }, { rejectWithValue }) => {
    try {
      const response = await userService.updateuser({
        params: data,
        userId,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

// Async thunk to delete a user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      await userService.deleteUser(userId);
      dispatch(getUsers()); // Refresh the users list after deletion
      return { userId, message: "user deleted successfully" }; // Pass message as well
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting user");
    }
  }
);

export const updateuser = createAsyncThunk(
  "users/updateuser",
  async ({ data, userId }, { rejectWithValue }) => {
    try {
      const response = await userService.updateuser({
        params: data,
        userId,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getGroup = createAsyncThunk(
  "users/getGroup",
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getGroup();
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const createGroup = createAsyncThunk(
  "users/createGroup",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await userService.createGroup(data);
      dispatch(getGroup()); // Refresh the users list after creation
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getGroupById = createAsyncThunk(
  "users/getGroupById",
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await userService.getGroupById(groupId);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const updateGroup = createAsyncThunk(
  "users/updateGroup",
  async ({ data, groupId }, { rejectWithValue }) => {
    try {
      const response = await userService.updateGroup({
        params: data,
        groupId,
      });
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const deleteGroup = createAsyncThunk(
  "users/deleteGroup",
  async (groupId, { rejectWithValue, dispatch }) => {
    try {
      await userService.deleteGroup(groupId);
      dispatch(getGroup()); // Refresh the users list after deletion
      return { groupId, message: "Group deleted successfully" }; // Pass message as well
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting group");
    }
  }
);

export const permissionList = createAsyncThunk(
  "users/permissionList",
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await userService.permissionList(groupId);
      return response;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const createPermission = createAsyncThunk(
  "users/createPermission",
  async ({ data, groupId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await userService.createPermission(data);
      dispatch(permissionList(groupId)); // Refresh the permissions list after creation
      return {
        success: true,
        message: "Permission created successfully",
        response,
      };
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const deletePermission = createAsyncThunk(
  "users/deletePermission",
  async ({ data, groupId }, { rejectWithValue, dispatch }) => {
    try {
      await userService.deletePermission(data);
      dispatch(permissionList(groupId)); // Refresh the permissions list after deletion
      return { success: true, message: "Permission deleted successfully" };
    } catch (err) {
      return rejectWithValue(err.message || "Error deleting permission");
    }
  }
);

// Create the slice for users
const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUsers.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.users = payload;
        state.showMessage = false;
      })
      .addCase(getUsers.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getUserProfileById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserProfileById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.selectedUser = payload; // ✅ Set selectedUser here
      })

      .addCase(updateUsers.pending, (state) => {
        state.loading = true;
      })

      .addCase(updateUsers.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(updateUsers.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      .addCase(getUserProfileById.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      .addCase(deleteUser.fulfilled, (state, { payload }) => {
        // Remove the deleted user from the users list
        state.users = state.users.filter(
          (user) => user.user_image_id !== payload.userId
        );
        state.message = payload.message; // Set success message
        state.showMessage = true;
      })
      .addCase(deleteUser.rejected, (state, { payload }) => {
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(getGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGroup.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.groups = payload;
        state.showMessage = false;
      })
      .addCase(getGroup.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(createGroup.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })
      .addCase(createGroup.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      })

      .addCase(permissionList.pending, (state) => {
        state.loading = true;
      })
      .addCase(permissionList.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.permissions = payload;
        state.showMessage = false;
      })
      .addCase(permissionList.rejected, (state, { payload }) => {
        state.loading = false;
        state.message = payload;
        state.showMessage = true;
      });
  },
});

// Export the actions and reducer
export const { clearMessage } = userSlice.actions;
export default userSlice.reducer;
